import React, { useRef, useState } from "react"
import { Link } from "gatsby"
import { SnailBrand, CompactReader } from "./RefinedShared"
import portrait from "../../../content/assets/profile-pic.jpg"
import QuietThemeControl from "./QuietThemeControl"
import "./quiet-notes.css"

const topics = ["All notes", "AI", "Systems", "Security", "Experiments", "Development"]
const keyOf = item => item.id || item.slug
const plain = value => String(value || "").replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, " ")
const two = value => String(value).padStart(2, "0")

function QuietHome({ posts, basePath }) {
  const [topic, setTopic] = useState("All notes")
  const [selectedKey, setSelectedKey] = useState(posts[0] ? keyOf(posts[0]) : null)
  const pointerFocus = useRef(false)
  const visible = topic === "All notes" ? posts : posts.filter(item => item.topic === topic)
  const selected = visible.find(item => keyOf(item) === selectedKey) || visible[0]
  const selectedNumber = selected && (selected.number || posts.length - posts.indexOf(selected))
  const years = posts.map(item => Number(item.year)).filter(Boolean)

  function filter(next) {
    setTopic(next)
    const first = posts.find(item => next === "All notes" || item.topic === next)
    setSelectedKey(first ? keyOf(first) : null)
  }

  return (
    <div className={`${basePath ? "concept-preview " : ""}quiet-theme qn-preview`} id="qn-top">
      <div className="qn-page">
        <header className="qn-header">
          <SnailBrand basePath={basePath} animated />
          <div className="qn-header-actions"><nav className="qn-nav" aria-label="Quiet Notes navigation">
            <a href="#qn-writing" aria-current="page">The writing</a>
            <a href="#qn-about">About Andrew <span aria-hidden="true">↗</span></a>
          </nav><QuietThemeControl /></div>
        </header>

        <main id="quiet-main">
          <section className="qn-introduction" aria-labelledby="qn-title">
            <div><h1 id="qn-title">A notebook, <em>kept open.</em><span aria-hidden="true">*</span></h1><p>Andrew’s notes on code, systems, and following the loose threads.</p></div>
            <span className="qn-introduction__count"><span className="qn-dot" />{posts.length} notes & counting</span>
          </section>

          <section className="qn-writing" id="qn-writing" aria-label="The writing">
            <div className="qn-topic-bar">
              <div className="qn-topics" aria-label="Filter notes by topic">
                {topics.map(item => <button type="button" key={item} aria-pressed={topic === item} onClick={() => filter(item)}>{item}{item === "All notes" && <sup>{posts.length}</sup>}</button>)}
              </div>
              <span className="qn-sort-label">Newest first ↓</span>
            </div>

            <div className="qn-notebook">
              <div className="qn-entries">
                <p className="qn-sr-only" role="status">Showing {visible.length} {topic === "All notes" ? "notes" : `${topic} notes`}.</p>
                {visible.map((item, index) => {
                  const selectedItem = selected && keyOf(item) === keyOf(selected)
                  const number = item.number || posts.length - posts.indexOf(item)
                  return (
                    <article className={`qn-entry${selectedItem ? " qn-entry--selected" : ""}`} key={keyOf(item)}>
                      <div className="qn-entry__rail"><span>{index === 0 || visible[index - 1].year !== item.year ? item.year : ""}</span><span>{two(number)}</span></div>
                      <div className="qn-entry__body">
                        <div className="qn-entry__meta"><span>{item.topic}</span><span>{item.timeToRead || 1} min read</span>{item === posts[0] && <span className="qn-latest">Latest</span>}</div>
                        <h2><button type="button" aria-pressed={selectedItem} aria-controls={`qn-abstract-${number} qn-margin-preview`} onPointerDown={() => { pointerFocus.current = true }} onPointerEnter={event => { if (event.pointerType === "mouse" && window.matchMedia("(min-width: 761px) and (hover: hover) and (pointer: fine)").matches) setSelectedKey(keyOf(item)) }} onFocus={() => { if (!pointerFocus.current) setSelectedKey(keyOf(item)) }} onBlur={() => { pointerFocus.current = false }} onClick={() => { setSelectedKey(keyOf(item)); pointerFocus.current = false }}>{item.title}</button></h2>
                        <div className="qn-entry__bottom"><time>{item.date}</time><Link to={`${basePath}${item.slug}`} aria-label={`Read ${item.title}`}>Read note <span aria-hidden="true">↗</span></Link></div>
                        <div className="qn-inline-preview" id={`qn-abstract-${number}`} hidden={!selectedItem}><p>{plain(item.description || item.excerpt)}</p><Link to={`${basePath}${item.slug}`}>Read the full note <span aria-hidden="true">→</span></Link></div>
                      </div>
                    </article>
                  )
                })}
                {!visible.length && <p className="qn-no-results">No notes here yet. Choose another thread.</p>}
                <div className="qn-end"><span aria-hidden="true">✳</span><p>{topic === "All notes" ? "The beginning of the notebook." : `That’s the ${topic.toLowerCase()} thread.`}</p>{topic !== "All notes" && <button type="button" onClick={() => filter("All notes")}>Return to all notes →</button>}</div>
              </div>

              <aside className="qn-margin" aria-label="Selected note preview">
                <div className="qn-margin__sticky">
                  <div className="qn-margin__heading"><span className="qn-label">In the margin</span><span aria-hidden="true">↙</span></div>
                  {selected && <div className="qn-margin__preview" key={keyOf(selected)} id="qn-margin-preview">
                    <span className="qn-note-number">Note {two(selectedNumber)} / {selected.topic}</span>
                    <h2>The short version.</h2>
                    <p>{plain(selected.description || selected.excerpt)}</p>
                    <Link className="qn-read-link" to={`${basePath}${selected.slug}`}>Read the full note <span aria-hidden="true">↗</span></Link>
                    <span className="qn-margin__time">{selected.timeToRead || 1} minutes, a little curiosity.</span>
                  </div>}
                  <p className="qn-margin__hint"><span aria-hidden="true">*</span> Hover, focus, or tap a title<br />to see what’s inside.</p>
                  <div className="qn-author-small"><img src={portrait} alt="Andrew on an open runway" loading="lazy" /><p>Written by Andrew.<br /><a href="#qn-about">A little more context ↗</a></p></div>
                </div>
              </aside>
            </div>
          </section>

          <section className="qn-about" id="qn-about" aria-labelledby="qn-about-title">
            <span className="qn-label">The person behind the notes</span>
            <div><h2 id="qn-about-title">Hi, I’m Andrew.</h2><p>A developer, systems tinkerer, and curious breaker of things. I write about AI, infrastructure, security, and the experiments that help me understand them.</p></div>
            <a href="#qn-top">Back to the top <span aria-hidden="true">↑</span></a>
          </section>
        </main>

        <footer className="qn-footer"><span>catch {"{"} snail {"}"} <span aria-hidden="true">·</span> A working record.</span><span>{years.length ? `${Math.min(...years)}—${Math.max(...years)}` : "Notes & experiments"}</span></footer>
      </div>
    </div>
  )
}

export default function QuietNotes({ posts = [], post, basePath = "/previews/quiet-notes" }) {
  if (post) return <CompactReader posts={posts} post={post} basePath={basePath} variant="quiet-notes" />
  return <QuietHome posts={posts} basePath={basePath} />
}
